import axios from 'axios';

import {
  getCookie,
  getJSONCookie,
  removeCookie,
  setCookie,
} from 'services/cookies';
import type { CoreMeResponse } from 'utils/coreAuth';

const ACCESS_EXPIRES_COOKIE = 'access_expires_at';
const REFRESH_MARGIN_MS = 2 * 60 * 1000; // renova 2 min antes de vencer
const REFRESH_JITTER_MS = 60 * 1000;
const RETRY_DELAY_MS = 60 * 1000;
const REFRESH_LOCK = 'tarelo-auth-refresh';
const SESSION_TIMEOUT_MS = 15 * 1000;

type CoreTokensResponse = {
  accessExpiresAt?: string;
};

// Instância sem interceptors: a renovação não pode disparar a própria renovação.
// O timeout impede que um gateway pendurado trave a carga do app, a renovação compartilhada
// e a trava entre abas; o timeout conta como core instável (sem resposta)
const sessionApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_CHAT,
  withCredentials: true,
  timeout: SESSION_TIMEOUT_MS,
});

/**
 * O access token é httpOnly, então a data de expiração (que não é segredo) fica num cookie
 * legível, compartilhado entre os apps, para que eles renovem a sessão antes de vencer.
 */
function saveAccessExpiry(tokens?: CoreTokensResponse) {
  if (tokens?.accessExpiresAt) {
    setCookie(ACCESS_EXPIRES_COOKIE, tokens.accessExpiresAt);
  }
}

const REFRESH_URL = 'v2/auth/refresh-token';

/**
 * Falha de rede (inclui 502 do gateway, que o navegador reporta como CORS), 5xx, timeout (408)
 * ou rate limit (429): o core está instável, o que não significa que a sessão seja inválida.
 */
export function isServiceUnavailable(error: unknown) {
  if (!axios.isAxiosError(error)) return false;

  const status = error.response?.status;

  return !status || status >= 500 || status === 408 || status === 429;
}

/**
 * Sessão recusada pelo core: 401/403 ou a renovação recusada com outro 4xx (ex.: refresh legado
 * inválido). Qualquer outro erro não prova que o usuário está deslogado.
 */
export function isSessionRejected(error: unknown) {
  if (!axios.isAxiosError(error) || isServiceUnavailable(error)) return false;

  const status = error.response?.status ?? 0;

  if (status === 401 || status === 403) return true;

  return error.config?.url === REFRESH_URL && status >= 400 && status < 500;
}

let refreshPromise: Promise<void> | null = null;

/**
 * Renova a sessão pelo cookie httpOnly (sem body). Se ainda houver o cookie legado `token`
 * (página aberta antes da migração), converte a sessão com o refresh token antigo.
 * Chamadas simultâneas compartilham a mesma renovação.
 */
export function refreshSession() {
  if (!refreshPromise) {
    const legacyRefreshToken = getLegacyRefreshToken();

    const refresh = legacyRefreshToken
      ? convertLegacySession(legacyRefreshToken)
      : sessionApi
          .post<CoreTokensResponse>(REFRESH_URL)
          .then(({ data }) => saveAccessExpiry(data));

    refreshPromise = refresh.finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

function getMsUntilRefresh() {
  const expiresAt = getCookie(ACCESS_EXPIRES_COOKIE);
  const expiresAtMs = expiresAt ? Date.parse(expiresAt) : Number.NaN;

  return Number.isNaN(expiresAtMs)
    ? null
    : expiresAtMs - Date.now() - REFRESH_MARGIN_MS;
}

/**
 * A trava só vale entre abas da mesma origem; entre apps (web, central, ichat) a coordenação
 * é pelo cookie compartilhado da data de expiração, que muda quando alguém renova.
 */
async function withRefreshLock(run: () => Promise<void>) {
  const { locks } = navigator as Navigator & { locks?: LockManager };

  if (locks) {
    await locks.request(REFRESH_LOCK, run);
  } else {
    await run();
  }
}

/**
 * Renova só se ainda for necessário: com a trava, outra aba pode ter renovado enquanto
 * esperávamos, e o refresh rotaciona o token.
 */
function refreshWhenDue() {
  return withRefreshLock(async () => {
    const msUntilRefresh = getMsUntilRefresh();

    if (msUntilRefresh !== null && msUntilRefresh > 0) return;

    await refreshSession();
  });
}

/**
 * Marca da sessão usada numa requisição (a data de expiração muda a cada renovação).
 */
export function getSessionSnapshot() {
  return getCookie(ACCESS_EXPIRES_COOKIE);
}

/**
 * Renovação após um 401: se a data mudou desde a requisição, outra aba ou app já renovou e
 * basta repetir. Renovar de novo com o mesmo refresh token rotacionado arrisca a sessão.
 */
export function refreshUnlessRenewed(snapshot?: string) {
  const isRenewed = () =>
    snapshot !== undefined && getSessionSnapshot() !== snapshot;

  if (getLegacyRefreshToken()) return refreshSession();
  if (isRenewed()) return Promise.resolve();

  return withRefreshLock(async () => {
    if (isRenewed()) return;

    await refreshSession();
  });
}

let refreshTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleRefresh(minDelayMs = 0) {
  clearTimeout(refreshTimer);

  const msUntilRefresh = getMsUntilRefresh();

  // Sem data de expiração (sessão anterior a esta versão): só a renovação ao receber 401
  if (msUntilRefresh === null) return;

  // Todos os apps leem o mesmo cookie e agendariam a renovação no mesmo instante; o jitter os
  // espalha (entre 2 e 1 min antes de vencer) para que o primeiro renove e os demais vejam a
  // data nova. Com o token já vencido, renova na hora
  const jitter =
    msUntilRefresh > 0 ? Math.round(Math.random() * REFRESH_JITTER_MS) : 0;

  refreshTimer = setTimeout(async () => {
    try {
      await refreshWhenDue();
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : 0;

      // Sessão inválida: quem cuida é o 401 da próxima requisição
      if (status === 401 || status === 403) return;

      scheduleRefresh(RETRY_DELAY_MS);

      return;
    }

    const next = getMsUntilRefresh();

    // Token recém-renovado ainda parecendo vencido (resposta sem accessExpiresAt ou relógio
    // do cliente adiantado): reagendar renovaria a cada minuto, rotacionando o token à toa.
    // Fica só a renovação ao receber 401
    if (next !== null && next <= 0) return;

    scheduleRefresh();
  }, Math.max(msUntilRefresh + jitter, minDelayMs));
}

// Timers atrasam em abas inativas: ao voltar ao foco, reavalia se já é hora de renovar
function onVisibilityChange() {
  if (document.visibilityState === 'visible') scheduleRefresh();
}

export function startAutoRefresh() {
  stopAutoRefresh();
  scheduleRefresh();
  document.addEventListener('visibilitychange', onVisibilityChange);
}

export function stopAutoRefresh() {
  clearTimeout(refreshTimer);
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

function getLegacyRefreshToken() {
  const legacyToken = getJSONCookie('token') as
    | { refresh_token?: string }
    | undefined;

  return legacyToken?.refresh_token;
}

/**
 * Sessões abertas antes da migração só têm o cookie legível `token` (tokens do PHP).
 * O core troca o refresh token antigo pelos cookies httpOnly; com 200, o cookie legado é descartado.
 */
async function convertLegacySession(refreshToken: string) {
  try {
    const { data } = await sessionApi.post<CoreTokensResponse>(REFRESH_URL, {
      refreshToken,
    });
    saveAccessExpiry(data);
  } catch (error) {
    // Refresh legado recusado: não adianta tentar de novo a cada carga
    if (isSessionRejected(error)) {
      removeCookie('token');
      removeCookie('usuario');
    }
    throw error;
  }

  removeCookie('token');
  removeCookie('usuario');
}

/**
 * Encerra a sessão no core, o único que consegue apagar os cookies httpOnly. Com o access token
 * vencido, renova antes: sem isso a sessão continua válida e o auth devolve o usuário para cá.
 */
export async function logoutSession() {
  try {
    await sessionApi.post('v2/auth/logout');
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : 0;
    if (status !== 401) throw error;

    // Refresh recusado: a sessão já não existe, não há o que encerrar
    try {
      await refreshSession();
    } catch (refreshError) {
      if (isSessionRejected(refreshError)) return;
      throw refreshError;
    }

    await sessionApi.post('v2/auth/logout');
  }
}

function fetchMe() {
  return sessionApi.get<CoreMeResponse>('v2/auth/me').then(({ data }) => data);
}

/**
 * Fonte da verdade da sessão: `GET /v2/auth/me`. Converte a sessão antiga (se houver) ou renova
 * um token prestes a vencer antes; se o `me` voltar 401, não há sessão.
 */
async function loadSessionUser() {
  const legacyRefreshToken = getLegacyRefreshToken();

  if (legacyRefreshToken) {
    await convertLegacySession(legacyRefreshToken);
  } else {
    // Renova antes do /me, pela data de expiração; um 401 no /me significa sem sessão
    const msUntilRefresh = getMsUntilRefresh();

    if (msUntilRefresh !== null && msUntilRefresh <= 0) {
      await refreshWhenDue();
    }
  }

  return fetchMe();
}

let sessionUserPromise: ReturnType<typeof loadSessionUser> | undefined;

/**
 * Montagens simultâneas (ex.: StrictMode em dev) compartilham a mesma busca, evitando /me duplicado.
 */
export function fetchSessionUser() {
  sessionUserPromise =
    sessionUserPromise ||
    loadSessionUser().finally(() => {
      sessionUserPromise = undefined;
    });

  return sessionUserPromise;
}
